// 03 §18 machines — sales, purchasing, inventory, production, dispatch, planning, channels.
// Aligned names: orders.approve_import→po_import.approve, orders.delete→orders.cancel,
// receiving.post→fg_receiving.post (06 §16 #16); programme actions use programmes.* (04 §16 #13).
import { defineMachine, systemStep as D, userStep as U } from './machine.ts';
import type { Transition } from './machine.ts';

export const PROGRAMME = defineMachine(
  'sales.programme.status',
  ['draft', 'planned', 'confirmed', 'in_production', 'shipped', 'closed', 'cancelled'],
  ['draft'],
  [
    U('draft', 'planned', 'plan', 'programmes.edit'),
    U('planned', 'confirmed', 'confirm', 'programmes.edit'),
    D('confirmed', 'in_production', 'orders_in_production'),
    D('in_production', 'shipped', 'orders_shipped'),
    U(['confirmed', 'in_production', 'shipped'], 'closed', 'close', 'programmes.edit'),
    U(['draft', 'planned', 'confirmed'], 'cancelled', 'cancel', 'programmes.edit', {
      reason: true,
    }),
  ],
);

const SO_REVISABLE = ['confirmed', 'partially_cut', 'cut', 'partially_shipped'] as const;
export const SALES_ORDER = defineMachine(
  'sales.sales_order.status',
  [
    'draft',
    'confirmed',
    'partially_cut',
    'cut',
    'partially_shipped',
    'shipped',
    'closed',
    'cancelled',
  ],
  ['draft'],
  [
    U('draft', 'confirmed', 'confirm', 'orders.confirm', { gates: ['G1'] }),
    D('confirmed', 'partially_cut', 'cut_progress'),
    D(['confirmed', 'partially_cut'], 'cut', 'fully_cut'),
    D(['partially_cut', 'cut'], 'partially_shipped', 'ship_progress'),
    D(['cut', 'partially_shipped'], 'shipped', 'fully_shipped'),
    D('shipped', 'closed', 'invoiced'),
    // CR-BG2d (R-ORD-SHP-1): a reopened shipment returns its pieces, so the derived status steps back; never from closed.
    D('shipped', 'partially_shipped', 'ship_reopened'),
    D(['shipped', 'partially_shipped'], 'cut', 'ship_reopened'),
    D(['shipped', 'partially_shipped'], 'partially_cut', 'ship_reopened'),
    D(['shipped', 'partially_shipped'], 'confirmed', 'ship_reopened'),
    U(['draft', 'confirmed'], 'cancelled', 'cancel', 'orders.cancel', { reason: true }),
    // revise keeps the status (revision row; qty ≥ shipped; approval)
    ...SO_REVISABLE.map((s): Transition<(typeof SO_REVISABLE)[number]> =>
      U(s, s, 'revise', 'orders.revise', { approvalLimit: true }),
    ),
  ],
);

const POI_OPEN = [
  'received',
  'parsed',
  'parse_failed',
  'duplicate',
  'validated',
  'exceptions',
] as const;
export const PO_IMPORT_DOCUMENT = defineMachine(
  'sales.po_import_document.status',
  [
    'received',
    'parsed',
    'parse_failed',
    'duplicate',
    'validated',
    'exceptions',
    'approved',
    'rejected',
    'superseded',
  ],
  ['received'],
  [
    D('received', 'parsed', 'parsed'),
    D('received', 'parse_failed', 'parse_failed'),
    D('received', 'duplicate', 'duplicate_detected'),
    D('parsed', 'validated', 'validated'),
    // CR-BG2d (W2d.po-import R-POI-CORE-1): a correction that raises a new exception reopens a validated import.
    D(['parsed', 'validated'], 'exceptions', 'exceptions_found'),
    D('exceptions', 'validated', 'exceptions_resolved'),
    // CR-BG2d (R-POI-CORE-1, BR-POI-026): an unapproved import is parsed again from its stored original.
    U(
      ['parsed', 'parse_failed', 'exceptions', 'validated'],
      'received',
      'reparse',
      'po_import.create',
    ),
    // G8: never automatic; an llm_fallback document additionally needs a reviewer (lane rule)
    U('validated', 'approved', 'approve', 'po_import.approve', { gates: ['G8'] }),
    U(POI_OPEN, 'rejected', 'reject', 'po_import.reject', { reason: true }),
    D('approved', 'superseded', 'newer_revision_approved'),
  ],
);

export const CUSTOMER_RETURN = defineMachine(
  'sales.customer_return.status',
  ['draft', 'received', 'closed', 'cancelled'],
  ['draft'],
  [
    U('draft', 'received', 'receive', 'fg_receiving.post'),
    D('received', 'closed', 'credit_issued'),
    U('draft', 'cancelled', 'cancel', 'fg_receiving.edit'),
  ],
);

export const PURCHASE_ORDER = defineMachine(
  'purchasing.purchase_order.status',
  ['draft', 'issued', 'confirmed', 'partially_received', 'received', 'closed', 'cancelled'],
  ['draft'],
  [
    U('draft', 'issued', 'issue', 'purchasing.create', { approvalLimit: true }),
    U('draft', 'issued', 'approve', 'purchasing.approve', { sod: true, approvalLimit: true }),
    U('issued', 'confirmed', 'confirm', 'purchasing.edit'),
    D(['issued', 'confirmed'], 'partially_received', 'receipt_posted'),
    D(['issued', 'confirmed', 'partially_received'], 'received', 'fully_received'),
    // CR-W2b-12 (CR-W2b-3 R-ENUM-1): a receipt reversal steps the derived status back (to partially received while
    // receipts remain, else to confirmed / issued as before the first receipt).
    D('received', 'partially_received', 'receipt_reversed'),
    D(['partially_received', 'received'], 'confirmed', 'receipt_reversed'),
    D(['partially_received', 'received'], 'issued', 'receipt_reversed'),
    U(
      ['issued', 'confirmed', 'partially_received', 'received'],
      'closed',
      'close',
      'purchasing.close',
      {
        reason: true,
      },
    ),
    // 05 BR-PUR-013: draft, issued or confirmed (no receipt) may be cancelled (CR-W2b-12, R-ENUM-1).
    U(['draft', 'issued', 'confirmed'], 'cancelled', 'cancel', 'purchasing.delete'),
  ],
);

const INBOUND = [
  'planned',
  'booked',
  'departed',
  'at_port',
  'customs_cleared',
  'delivered',
] as const;
type Inbound = (typeof INBOUND)[number] | 'cancelled';
/** Forward only — derived from the latest actual eta_event, or set by purchasing.edit. */
const inboundForward: Transition<Inbound>[] = INBOUND.slice(1).flatMap((to, i) => {
  const earlier = INBOUND.slice(0, i + 1);
  return [D(earlier, to, 'eta_actual'), U(earlier, to, 'advance', 'purchasing.edit')];
});
export const INBOUND_SHIPMENT = defineMachine(
  'purchasing.inbound_shipment.status',
  [...INBOUND, 'cancelled'],
  ['planned'],
  [...inboundForward, U(['planned', 'booked'], 'cancelled', 'cancel', 'purchasing.edit')],
);

export const GOODS_RECEIPT = defineMachine(
  'purchasing.goods_receipt.status',
  ['draft', 'posted', 'reversed'],
  ['draft'],
  [
    U('draft', 'posted', 'post', 'purchasing.receive'),
    U('posted', 'reversed', 'reverse', 'purchasing.reverse', { reason: true }),
  ],
);

export const STOCK_ADJUSTMENT = defineMachine(
  'inventory.stock_adjustment.status',
  ['draft', 'submitted', 'approved', 'posted', 'cancelled'],
  ['draft'],
  [
    U('draft', 'submitted', 'submit', 'inventory.create'),
    U('submitted', 'approved', 'approve', 'inventory.approve', { sod: true, approvalLimit: true }),
    U(['submitted', 'approved'], 'posted', 'post', 'inventory.adjust', { approvalLimit: true }),
    U(['draft', 'submitted'], 'cancelled', 'cancel', 'inventory.create'),
  ],
);

export const WAREHOUSE_DOCUMENT = defineMachine(
  'inventory.warehouse_document.status',
  ['draft', 'ready', 'applied', 'cancelled'],
  ['draft'],
  [
    U('draft', 'ready', 'mark_ready', 'warehouse.edit'),
    U('ready', 'applied', 'apply', 'warehouse.apply'),
    U(['draft', 'ready'], 'cancelled', 'cancel', 'warehouse.delete'),
  ],
);

export const STOCK_RESERVATION = defineMachine(
  'inventory.stock_reservation.status',
  ['open', 'partially_consumed', 'consumed', 'released', 'cancelled'],
  ['open'],
  [
    D('open', 'partially_consumed', 'partially_issued'),
    D(['open', 'partially_consumed'], 'consumed', 'fully_issued'),
    D('open', 'released', 'demand_cut_or_cancelled'),
    U('open', 'cancelled', 'cancel', 'inventory.adjust', { reason: true }),
  ],
);

export const CUTTING_SLIP = defineMachine(
  'production.cutting_slip.status',
  ['draft', 'held', 'released', 'cut', 'complete', 'cancelled'],
  ['draft'],
  [
    U('draft', 'held', 'hold', 'cutting.edit'),
    U('held', 'released', 'release', 'cutting.release', { sod: true, gates: ['G2', 'G5', 'G9'] }),
    D('released', 'cut', 'cut_report_posted'),
    D('cut', 'complete', 'allocations_closed'),
    // CR-BG2e (W2e.sewing-fg R-CUT-REV-1): reversing an FG receipt of a completed cut reopens it (system step; the
    // receipt reversal carries the reason).
    D('complete', 'cut', 'fg_receipt_reversed'),
    U(['draft', 'held'], 'cancelled', 'cancel', 'cutting.delete'),
    U('released', 'cancelled', 'cancel_released', 'cutting.override', {
      overrideOnly: true,
      reason: true,
    }),
  ],
);

export const CUT_REPORT = defineMachine(
  'production.cut_report.status',
  ['draft', 'posted', 'reversed'],
  ['draft'],
  [
    U('draft', 'posted', 'post', 'cutting.edit'),
    U('posted', 'reversed', 'reverse', 'cutting.override', { overrideOnly: true, reason: true }),
  ],
);

export const FG_RECEIPT = defineMachine(
  'production.fg_receipt.status',
  ['draft', 'posted', 'reversed'],
  ['draft'],
  [
    U('draft', 'posted', 'post', 'fg_receiving.post'),
    U('posted', 'reversed', 'reverse', 'fg_receiving.override', {
      overrideOnly: true,
      reason: true,
    }),
  ],
);

/**
 * issued→in_progress is derived from the first FG receipt [assumption: 03 omits it]. CR-BG2e (W2e.sewing-fg R-CORE-SEW-3,
 * 06 §7.3): an issued allocation may be closed short or cancelled (`sewing.close`; cancel only while nothing was
 * received), and a reversed FG receipt moves it back (`fg_receipt_reversed`, system: to `in_progress` while something
 * stays received, else to `issued` — resolve with the target state).
 */
export const SEWING_ALLOCATION = defineMachine(
  'production.sewing_allocation.status',
  ['open', 'issued', 'in_progress', 'complete', 'closed_short', 'cancelled'],
  ['open'],
  [
    U('open', 'issued', 'issue', 'sewing.issue'),
    D('issued', 'in_progress', 'first_receipt'),
    D('in_progress', 'complete', 'fully_received'),
    U(['issued', 'in_progress'], 'closed_short', 'close_short', 'sewing.close', { reason: true }),
    U('open', 'cancelled', 'cancel', 'sewing.edit'),
    U('issued', 'cancelled', 'cancel', 'sewing.close'),
    D('complete', 'in_progress', 'fg_receipt_reversed'),
    D(['in_progress', 'complete'], 'issued', 'fg_receipt_reversed'),
  ],
);

export const SHIPMENT = defineMachine(
  'dispatch.shipment.status',
  ['draft', 'picking', 'packed', 'ready', 'dispatched', 'delivered', 'cancelled'],
  ['draft'],
  [
    U('draft', 'picking', 'start_picking', 'shipping.edit'),
    U('picking', 'packed', 'pack', 'shipping.edit'),
    U('packed', 'ready', 'mark_ready', 'shipping.edit'),
    U('ready', 'dispatched', 'dispatch', 'shipping.dispatch', { gates: ['G3', 'G4'] }),
    D('dispatched', 'delivered', 'delivered'),
    U(['draft', 'picking', 'packed', 'ready'], 'cancelled', 'cancel', 'shipping.edit'),
    // CR-BG2d (W2d.shipping R-CORE-SHP-3): BR-SHP-013 reopen of a dispatched shipment (override only, with a reason; the
    // issue is reversed with `shipment_reversal`) and a ready shipment back to packed for a correction.
    U('dispatched', 'ready', 'reopen', 'shipping.override', { overrideOnly: true, reason: true }),
    U('ready', 'packed', 'unready', 'shipping.edit'),
  ],
);

export const LABEL_RUN = defineMachine(
  'dispatch.label_run.status',
  ['draft', 'queued', 'printed', 'failed', 'cancelled'],
  ['draft'],
  [
    U('draft', 'queued', 'print', 'labels.print'),
    D('queued', 'printed', 'agent_printed'),
    D('queued', 'failed', 'agent_failed'),
    U('failed', 'queued', 'retry', 'labels.print'),
    U('draft', 'cancelled', 'cancel', 'labels.edit'),
  ],
);

export const CREDIT_DEBIT_NOTE = defineMachine(
  'dispatch.credit_debit_note.status',
  ['draft', 'approved', 'posted', 'cancelled'],
  ['draft'],
  [
    U('draft', 'approved', 'approve', 'notes.approve', { sod: true }),
    U('approved', 'posted', 'post', 'notes.post'),
    U('draft', 'cancelled', 'cancel', 'notes.edit'),
  ],
);

export const REQUIREMENT_RUN = defineMachine(
  'planning.requirement_run.status',
  ['running', 'complete', 'failed', 'superseded'],
  ['running'],
  [
    D('running', 'complete', 'run_complete'),
    D('running', 'failed', 'run_failed'),
    D('complete', 'superseded', 'newer_run'),
  ],
);

const D2C_PRE_FULFIL = ['received', 'allocated', 'picking', 'packed'] as const;
export const D2C_ORDER = defineMachine(
  'channels.d2c_order.status',
  [
    'received',
    'allocated',
    'picking',
    'packed',
    'fulfilled',
    'partially_fulfilled',
    'cancelled',
    'partially_returned',
    'returned',
    'closed',
  ],
  ['received'],
  [
    D('received', 'allocated', 'allocated'),
    U('allocated', 'picking', 'pick', 'd2c.fulfil'),
    U('picking', 'packed', 'pack', 'd2c.fulfil'),
    U(['packed', 'partially_fulfilled'], 'fulfilled', 'fulfil', 'd2c.fulfil'),
    U('packed', 'partially_fulfilled', 'fulfil_partial', 'd2c.fulfil'),
    D(D2C_PRE_FULFIL, 'cancelled', 'cancelled_webhook'),
    D(['fulfilled', 'partially_fulfilled'], 'partially_returned', 'partial_refund'),
    D(['fulfilled', 'partially_fulfilled', 'partially_returned'], 'returned', 'full_refund'),
    D(
      ['fulfilled', 'partially_fulfilled', 'partially_returned', 'returned', 'cancelled'],
      'closed',
      'payout_reconciled',
    ),
  ],
);

export const SHOPIFY_VARIANT_MAP = defineMachine(
  'channels.shopify_variant_map.status',
  ['unmatched', 'mapped', 'ignored', 'conflict'],
  ['unmatched'],
  [
    U(['unmatched', 'conflict'], 'mapped', 'map', 'd2c.edit'),
    U('unmatched', 'ignored', 'ignore', 'd2c.edit'),
    D('mapped', 'conflict', 'sync_conflict'),
    // CR-BG2f (W2f.d2c-sync R8): a wrong confirmation is undone and an ignored variant reopened, with a reason.
    U('mapped', 'unmatched', 'unmap', 'd2c.edit', { reason: true }),
    U('ignored', 'unmatched', 'reopen', 'd2c.edit', { reason: true }),
  ],
);
